/**
 * Community group directory + map — sections/community-group-list.liquid
 *
 * Reads the PUBLIC, credential-free mi-core API:
 *   GET /api/public/community-groups          → { data, total, offset, limit }
 *   GET /api/public/community-groups/geojson  → FeatureCollection of Point features
 *
 * The contract is the source of truth and lives in mi-core at
 * `docs/community-groups-api.md`. It is referenced, never restated here.
 *
 * Rules this file exists to keep:
 * - No credentials, ever. `credentials: 'omit'` on every request.
 * - No caching layer in front of either endpoint. Both send `Cache-Control:
 *   no-store` so a leader's withdrawn email disappears on the next read; a
 *   sessionStorage/localStorage cache would undo that.
 * - No fallback to the Shopify `community_group` metaobjects on failure — that
 *   would republish contact details a leader may have withdrawn. Show an error.
 * - Descriptions are plain text. Rows are built with createElement/textContent;
 *   API data never reaches innerHTML.
 * - A group without coordinates is never silently dropped: it stays in the
 *   list, and while the map narrows the list it gets its own "not on the map"
 *   group. An empty map must not look like a failure — see renderMapPanel().
 */
;(function () {
  'use strict'

  var CONFIG = window.communityGroupsConfig || {}
  var T = CONFIG.t || {}
  var BASE = (window.TMI_CORE_API && window.TMI_CORE_API.base) || ''

  var PAGE = 250 // contract maximum
  var MAX_REQUESTS = 10 // runaway guard; 144 groups = 1 request
  var FETCH_TIMEOUT = 15000
  var LIST_PAGE = 10 // rows shown before "Show more"

  var LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
  var LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
  // Published on https://leafletjs.com/download.html for 1.9.4. An SRI mismatch
  // fails the load, which degrades to the neutral "map unavailable" panel.
  var LEAFLET_CSS_SRI = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY='
  var LEAFLET_JS_SRI = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo='
  // Pin clustering. Optional: if it fails to load, pins render unclustered.
  // SRI computed from the unpkg/jsdelivr 1.5.3 dist file (both serve the same bytes).
  var CLUSTER_JS = 'https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js'
  var CLUSTER_JS_SRI = 'sha256-Hk4dIpcqOSb0hZjgyvFOP+cEmDXUKKNE/tT542ZbNQg='

  var els = {}
  var state = {
    groups: [],
    total: 0,
    pointsBySlug: {}, // slug → { lat, lng }
    meetingType: '', // chip filter
    visible: LIST_PAGE, // rows currently revealed
    map: null,
    markerLayer: null,
    leafletFailed: false,
    area: null, // L.LatLngBounds once the visitor pans/zooms; narrows the list
    fitting: false, // true while this file moves the map itself
    userMoving: false,
  }

  /* ------------------------------------------------------------------ utils */

  function timeoutSignal(ms) {
    if (typeof AbortController === 'undefined') return undefined
    var controller = new AbortController()
    setTimeout(function () {
      controller.abort()
    }, ms)
    return controller.signal
  }

  // The only place a string is allowed to become a URL. http(s) only; `mailto:`
  // links are built by this file from an address, never parsed out of input.
  function safeUrl(raw) {
    var value = String(raw == null ? '' : raw).trim()
    return /^https?:\/\//i.test(value) ? value : ''
  }

  // Liquid's `t` filter HTML-escapes a translation when it interpolates
  // (`don't` → `don&#39;t`). These strings are rendered with textContent, so
  // decode the entities back to plain text first. DOMParser never runs scripts.
  function decodeEntities(text) {
    if (text.indexOf('&') === -1) return text
    return new DOMParser().parseFromString(text, 'text/html').documentElement.textContent
  }

  function fill(template, replacements) {
    var out = decodeEntities(String(template == null ? '' : template))
    Object.keys(replacements).forEach(function (key) {
      out = out.split(key).join(String(replacements[key]))
    })
    return out
  }

  function el(tag, className, text) {
    var node = document.createElement(tag)
    if (className) node.className = className
    if (text != null && text !== '') node.textContent = String(text)
    return node
  }

  function clear(node) {
    while (node && node.firstChild) node.removeChild(node.firstChild)
  }

  function meetingTypeLabel(code) {
    if (!code) return ''
    var labels = T.meetingType || {}
    return labels[code] || ''
  }

  function locationLine(group) {
    var place = [group.city, group.region].filter(Boolean).join(', ')
    var countryName = group.country && group.country.name ? group.country.name : ''
    return [place, countryName].filter(Boolean).join(' · ')
  }

  function languageNames(group) {
    return (group.languages || [])
      .map(function (lang) {
        return lang && (lang.name || lang.code)
      })
      .filter(Boolean)
  }

  function groupCount(n) {
    return n === 1 ? String(T.groupCountOne || '1') : fill(T.groupCountOther, { __COUNT__: n })
  }

  /* ------------------------------------------------------------------ fetch */

  function loadGroups() {
    var all = []
    var offset = 0
    var total = Infinity
    var requests = 0

    function next() {
      if (all.length >= total || requests >= MAX_REQUESTS) {
        if (all.length < total) {
          console.warn('community-groups: loaded ' + all.length + ' of ' + total)
        }
        return { groups: all, total: isFinite(total) ? total : all.length }
      }

      var url = BASE + '/api/public/community-groups?offset=' + offset + '&limit=' + PAGE
      return fetch(url, { credentials: 'omit', cache: 'no-store', signal: timeoutSignal(FETCH_TIMEOUT) })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status)
          return res.json()
        })
        .then(function (page) {
          all = all.concat(page.data || [])
          total = typeof page.total === 'number' ? page.total : all.length
          offset += PAGE
          requests += 1
          return next()
        })
    }

    return Promise.resolve().then(next)
  }

  // Primary source of pins. On failure, fall back to the directory payload's own
  // `location` field so a geojson outage degrades to a working map rather than a
  // broken one — the two can never disagree, they are the same coordinates.
  function loadPoints(groups) {
    var url = BASE + '/api/public/community-groups/geojson'
    return fetch(url, { credentials: 'omit', cache: 'no-store', signal: timeoutSignal(FETCH_TIMEOUT) })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status)
        return res.json()
      })
      .then(function (collection) {
        var bySlug = {}
        ;(collection.features || []).forEach(function (feature) {
          var props = feature.properties || {}
          var coords = (feature.geometry && feature.geometry.coordinates) || []
          // GeoJSON is [longitude, latitude] — not the other way round.
          var lng = Number(coords[0])
          var lat = Number(coords[1])
          if (!props.slug || !isFinite(lat) || !isFinite(lng)) return
          bySlug[props.slug] = { lat: lat, lng: lng }
        })
        return bySlug
      })
      .catch(function (error) {
        console.warn('community-groups: geojson unavailable, deriving points from the directory', error)
        var bySlug = {}
        groups.forEach(function (group) {
          var loc = group.location
          if (!loc || !group.slug) return
          var lat = Number(loc.latitude)
          var lng = Number(loc.longitude)
          if (!isFinite(lat) || !isFinite(lng)) return
          bySlug[group.slug] = { lat: lat, lng: lng }
        })
        return bySlug
      })
  }

  /* ---------------------------------------------------------------- filters */

  function currentFilters() {
    return {
      q: (els.search.value || '').trim().toLowerCase(),
      meetingType: state.meetingType,
      language: els.language && !els.language.hidden ? els.language.value : '',
    }
  }

  function anyFilterActive(f) {
    return Boolean(f.q || f.meetingType || f.language)
  }

  // Leaders mostly enter a US or Canadian region as its postal code (`FL`), but
  // visitors search by name ("Florida"), which a substring search alone misses.
  // Keyed by country code so `CA` is California only for a US group. mi-core's
  // `community-group-regions.ts` carries the same table for the server's `q`;
  // keep the two in step.
  var REGIONS = {
    US: {
      AL: 'Alabama',
      AK: 'Alaska',
      AZ: 'Arizona',
      AR: 'Arkansas',
      CA: 'California',
      CO: 'Colorado',
      CT: 'Connecticut',
      DE: 'Delaware',
      DC: 'District of Columbia',
      FL: 'Florida',
      GA: 'Georgia',
      HI: 'Hawaii',
      ID: 'Idaho',
      IL: 'Illinois',
      IN: 'Indiana',
      IA: 'Iowa',
      KS: 'Kansas',
      KY: 'Kentucky',
      LA: 'Louisiana',
      ME: 'Maine',
      MD: 'Maryland',
      MA: 'Massachusetts',
      MI: 'Michigan',
      MN: 'Minnesota',
      MS: 'Mississippi',
      MO: 'Missouri',
      MT: 'Montana',
      NE: 'Nebraska',
      NV: 'Nevada',
      NH: 'New Hampshire',
      NJ: 'New Jersey',
      NM: 'New Mexico',
      NY: 'New York',
      NC: 'North Carolina',
      ND: 'North Dakota',
      OH: 'Ohio',
      OK: 'Oklahoma',
      OR: 'Oregon',
      PA: 'Pennsylvania',
      RI: 'Rhode Island',
      SC: 'South Carolina',
      SD: 'South Dakota',
      TN: 'Tennessee',
      TX: 'Texas',
      UT: 'Utah',
      VT: 'Vermont',
      VA: 'Virginia',
      WA: 'Washington',
      WV: 'West Virginia',
      WI: 'Wisconsin',
      WY: 'Wyoming',
      AS: 'American Samoa',
      GU: 'Guam',
      MP: 'Northern Mariana Islands',
      PR: 'Puerto Rico',
      VI: 'U.S. Virgin Islands',
    },
    CA: {
      AB: 'Alberta',
      BC: 'British Columbia',
      MB: 'Manitoba',
      NB: 'New Brunswick',
      NL: 'Newfoundland and Labrador',
      NS: 'Nova Scotia',
      NT: 'Northwest Territories',
      NU: 'Nunavut',
      ON: 'Ontario',
      PE: 'Prince Edward Island',
      QC: 'Quebec',
      SK: 'Saskatchewan',
      YT: 'Yukon',
    },
  }

  // Lower-cased, with stray edge punctuation dropped — stored data includes `West Bengal,`.
  function normalizeRegion(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/^[\s.,;]+|[\s.,;]+$/g, '')
  }

  // The group's region as { code, name } when it is a known code or name for its
  // country, else null.
  function resolveRegion(group) {
    var countryCode = group.country && group.country.code
    var table = countryCode ? REGIONS[String(countryCode).toUpperCase()] : null
    var value = normalizeRegion(group.region)
    if (!table || !value) return null
    for (var code in table) {
      if (!Object.prototype.hasOwnProperty.call(table, code)) continue
      if (code.toLowerCase() === value || table[code].toLowerCase() === value) {
        return { code: code, name: table[code] }
      }
    }
    return null
  }

  // The full name contains the query ("flor" → `FL`), or the query is exactly the
  // code or name of the same region ("IL" → `Illinois`). A bare code only matches
  // exactly, so typing two letters doesn't turn into a state.
  function regionMatches(group, q) {
    var resolved = resolveRegion(group)
    var query = normalizeRegion(q)
    if (!resolved || !query) return false
    return resolved.name.toLowerCase().indexOf(query) !== -1 || resolved.code.toLowerCase() === query
  }

  // Whether the query names this group's whole region, by code or by name.
  function namesRegion(group, q) {
    var query = normalizeRegion(q)
    if (!query) return false
    if (normalizeRegion(group.region) === query) return true
    var resolved = resolveRegion(group)
    return Boolean(resolved && (resolved.code.toLowerCase() === query || resolved.name.toLowerCase() === query))
  }

  // Mirrors what the server's `q` searches, so behaviour matches the contract if
  // we ever move filtering server-side. Country and region are in the haystack,
  // which is what replaced the old country/region dropdowns.
  function matchesQuery(group, q) {
    if (!q) return true
    if (regionMatches(group, q)) return true
    var haystack = [group.name, group.city, group.region, group.country && group.country.name]
    ;(group.leaders || []).forEach(function (leader) {
      haystack.push(leader && leader.name)
    })
    return haystack.some(function (value) {
      return value && String(value).toLowerCase().indexOf(q) !== -1
    })
  }

  function applyFilters() {
    var f = currentFilters()
    return state.groups.filter(function (group) {
      if (!matchesQuery(group, f.q)) return false
      if (f.meetingType && group.meetingType !== f.meetingType) return false
      if (f.language) {
        var codes = (group.languages || []).map(function (lang) {
          return lang && lang.code
        })
        if (codes.indexOf(f.language) === -1) return false
      }
      return true
    })
  }

  function byLabel(a, b) {
    return String(a.label).localeCompare(String(b.label))
  }

  // Chips for each meeting type present in the data, in a fixed order.
  function renderTypeChips() {
    var present = {}
    state.groups.forEach(function (group) {
      if (group.meetingType) present[group.meetingType] = true
    })

    clear(els.types)
    ;['IN_PERSON', 'ONLINE', 'HYBRID'].forEach(function (code) {
      if (!present[code]) return
      var active = state.meetingType === code
      var chip = el(
        'button',
        'rounded-full border px-3 py-1.5 text-xs leading-none font-medium tracking-[0.1em] uppercase cursor-pointer transition-colors ' +
          (active
            ? 'border-mi-grey-azure bg-mi-sea-light text-mi-deep-green'
            : 'border-mi-grey-200 bg-white text-mi-grey-500 hover:border-mi-grey-azure hover:text-mi-deep-green'),
        meetingTypeLabel(code) || code,
      )
      chip.type = 'button'
      chip.setAttribute('aria-pressed', active ? 'true' : 'false')
      chip.addEventListener('click', function () {
        state.meetingType = active ? '' : code
        renderTypeChips()
        update()
      })
      els.types.appendChild(chip)
    })
  }

  // Dedupe languages on `code`, never on `name`: the imported lookup table has
  // odd code slugs, but the display names are correct. Hidden unless there is a
  // real choice to make.
  function populateLanguages() {
    var languages = {}
    state.groups.forEach(function (group) {
      ;(group.languages || []).forEach(function (lang) {
        if (lang && lang.code) languages[lang.code] = lang.name || lang.code
      })
    })

    var options = Object.keys(languages)
      .map(function (code) {
        return { value: code, label: languages[code] }
      })
      .sort(byLabel)

    var select = els.language
    var previous = select.value
    var placeholder = select.getAttribute('data-placeholder')
    clear(select)
    select.appendChild(el('option', null, placeholder))
    select.firstChild.value = ''
    options.forEach(function (option) {
      var node = el('option', null, option.label)
      node.value = option.value
      select.appendChild(node)
    })
    select.value = previous
    if (select.value !== previous) select.value = ''
    select.hidden = options.length < 2
  }

  /* ----------------------------------------------------------------- render */

  function svg(attrs, paths) {
    var NS = 'http://www.w3.org/2000/svg'
    var node = document.createElementNS(NS, 'svg')
    Object.keys(attrs).forEach(function (key) {
      node.setAttribute(key, attrs[key])
    })
    node.setAttribute('aria-hidden', 'true')
    paths.forEach(function (attrsForPath) {
      var path = document.createElementNS(NS, 'path')
      Object.keys(attrsForPath).forEach(function (key) {
        path.setAttribute(key, attrsForPath[key])
      })
      node.appendChild(path)
    })
    return node
  }

  function personPlaceholder() {
    var wrapper = el('div', 'w-9 h-9 flex-none rounded-full bg-mi-sea-light flex items-center justify-center')
    wrapper.appendChild(
      svg({ class: 'w-5 h-5 text-mi-grey-azure', fill: 'currentColor', viewBox: '0 0 20 20' }, [
        {
          'fill-rule': 'evenodd',
          'clip-rule': 'evenodd',
          d: 'M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z',
        },
      ]),
    )
    return wrapper
  }

  function textLink(href, label, external) {
    var link = el(
      'a',
      'text-[13px] font-medium text-mi-deep-green underline underline-offset-[3px] hover:text-mi-forest',
      label,
    )
    link.href = href
    if (external) {
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      link.appendChild(el('span', 'sr-only', ' — ' + T.opensNewWindow))
    }
    return link
  }

  // Expanded part of a row: full description, schedule/subject, leaders and
  // contact links. Built on first open.
  function buildDetails(group) {
    var panel = el('div', 'mt-4 flex flex-col gap-4 border-t border-mi-grey-100 pt-4')

    // Plain text, by contract. textContent, never innerHTML.
    if (group.description) {
      panel.appendChild(el('p', 'm-0 text-sm leading-[1.55] text-mi-charcoal whitespace-pre-line', group.description))
    }
    if (group.subject) {
      panel.appendChild(el('p', 'm-0 text-sm leading-[1.55] text-mi-charcoal', group.subject))
    }

    var contact = el('div', 'flex flex-wrap gap-x-5 gap-y-2')
    // `email` is absent, not null, when unset — truthiness is the documented check.
    if (group.email) contact.appendChild(textLink('mailto:' + group.email, T.email, false))
    ;(group.links || []).forEach(function (link) {
      var href = safeUrl(typeof link === 'string' ? link : link && link.url)
      if (href) contact.appendChild(textLink(href, T.website, true))
    })
    if (contact.childNodes.length) panel.appendChild(contact)

    var leaders = (group.leaders || []).filter(Boolean)
    if (leaders.length) {
      var block = el('div')
      block.appendChild(
        el(
          'h4',
          'm-0 mb-2.5 text-[11px] leading-none font-medium tracking-[0.2em] uppercase text-mi-deep-green',
          T.leaders,
        ),
      )
      var list = el('div', 'flex flex-col gap-2.5')
      leaders.forEach(function (leader) {
        var row = el('div', 'flex items-center gap-3')
        var imageUrl = safeUrl(leader.imageUrl || leader.image)
        if (imageUrl) {
          var img = el('img', 'w-9 h-9 flex-none rounded-full object-cover')
          img.src = imageUrl
          img.alt = leader.name || ''
          img.loading = 'lazy'
          img.width = 36
          img.height = 36
          img.referrerPolicy = 'no-referrer'
          row.appendChild(img)
        } else {
          row.appendChild(personPlaceholder())
        }

        var who = el('div', 'min-w-0')
        who.appendChild(el('p', 'm-0 text-sm leading-snug font-medium text-mi-charcoal truncate', leader.name || ''))
        if (leader.email) {
          var mail = el(
            'a',
            'block text-xs text-mi-deep-green underline underline-offset-2 truncate hover:text-mi-forest',
            leader.email,
          )
          mail.href = 'mailto:' + leader.email
          who.appendChild(mail)
        }
        row.appendChild(who)
        list.appendChild(row)
      })
      block.appendChild(list)
      panel.appendChild(block)
    }

    return panel
  }

  function buildRow(group) {
    var row = el('article', 'scroll-mt-28 border-b border-mi-grey-100 px-5 py-5 lg:px-7')
    if (group.slug) row.id = 'cg-group-' + group.slug

    var grid = el('div', 'grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4')
    row.appendChild(grid)

    var main = el('div', 'flex min-w-0 flex-col gap-[5px]')
    main.appendChild(el('h3', 'm-0 text-base leading-snug font-bold text-mi-forest', group.name || ''))

    var meta = [locationLine(group), meetingTypeLabel(group.meetingType), group.schedule].filter(Boolean).join(' · ')
    if (meta) main.appendChild(el('p', 'm-0 text-[13px] leading-snug text-mi-grey-500', meta))

    if (group.description) {
      main.appendChild(el('p', 'm-0 text-sm leading-[1.45] text-mi-charcoal line-clamp-2', group.description))
    }
    grid.appendChild(main)

    var side = el('div', 'flex flex-col items-end gap-1.5 text-right')
    var langs = languageNames(group)
    if (langs.length) side.appendChild(el('p', 'm-0 text-[13px] leading-snug text-mi-grey-500', langs.join(', ')))

    var toggle = el(
      'button',
      'cursor-pointer whitespace-nowrap border-0 bg-transparent p-0 text-xs font-medium tracking-[0.1em] uppercase text-mi-deep-green hover:text-mi-forest',
    )
    toggle.type = 'button'
    toggle.setAttribute('aria-expanded', 'false')
    var toggleLabel = el('span', null, T.details)
    toggle.appendChild(toggleLabel)
    toggle.appendChild(el('span', null, ' →'))
    toggle.lastChild.setAttribute('aria-hidden', 'true')
    side.appendChild(toggle)
    grid.appendChild(side)

    var details = null
    function setOpen(open) {
      if (open && !details) {
        details = buildDetails(group)
        if (group.slug) {
          details.id = 'cg-details-' + group.slug
          toggle.setAttribute('aria-controls', details.id)
        }
        row.appendChild(details)
      }
      if (details) {
        // The panel is `flex`, which beats the [hidden] attribute's display:none,
        // so toggle Tailwind's `hidden` class too.
        details.hidden = !open
        details.classList.toggle('hidden', !open)
      }
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false')
      toggleLabel.textContent = open ? T.hideDetails : T.details
    }
    toggle.addEventListener('click', function () {
      setOpen(toggle.getAttribute('aria-expanded') !== 'true')
    })
    row.cgOpen = function () {
      setOpen(true)
    }

    return row
  }

  function button(label, variant) {
    var classes =
      variant === 'outline'
        ? 'rounded border border-mi-deep-green bg-white px-[26px] py-[13px] text-xs leading-none font-medium tracking-[0.12em] uppercase text-mi-deep-green hover:bg-mi-sea-light cursor-pointer'
        : 'rounded-full border border-mi-grey-200 bg-white px-3 py-1.5 text-xs leading-none font-medium tracking-[0.1em] uppercase text-mi-grey-500 hover:border-mi-grey-azure hover:text-mi-deep-green cursor-pointer'
    var node = el('button', classes, label)
    node.type = 'button'
    return node
  }

  function clearFiltersButton() {
    var node = button(T.clearFilters)
    node.addEventListener('click', function () {
      els.search.value = ''
      els.language.value = ''
      state.meetingType = ''
      renderTypeChips()
      update()
    })
    return node
  }

  function resetMapButton(variant) {
    var node = button(T.resetMap, variant)
    node.addEventListener('click', update)
    return node
  }

  function retryButton() {
    var node = button(T.retry)
    node.addEventListener('click', init)
    return node
  }

  // `offMap`: groups matching the filters that have no coordinates. Only passed
  // while the map area narrows the list, so they are never silently dropped.
  function renderResults(groups, offMap) {
    offMap = offMap || []
    clear(els.results)

    if (!groups.length) {
      var empty = el('div', 'flex flex-col items-center gap-4 px-5 py-12 text-center lg:px-7')
      empty.appendChild(el('p', 'm-0 text-sm text-mi-grey-500', state.area ? T.noResultsInArea : T.noResults))
      empty.appendChild(state.area ? resetMapButton('outline') : clearFiltersButton())
      els.results.appendChild(empty)
    } else {
      var shown = groups.slice(0, state.visible)
      shown.forEach(function (group) {
        els.results.appendChild(buildRow(group))
      })

      if (groups.length > shown.length) {
        var more = el('div', 'flex justify-center px-5 py-[22px] lg:px-7')
        var moreButton = button(T.showMore, 'outline')
        moreButton.addEventListener('click', function () {
          state.visible += LIST_PAGE
          refreshList()
        })
        more.appendChild(moreButton)
        els.results.appendChild(more)
      }
    }

    if (offMap.length) {
      var heading = el(
        'h3',
        'm-0 border-b border-mi-grey-100 bg-mi-grey-50 px-5 py-3 text-[11px] leading-none font-medium tracking-[0.2em] uppercase text-mi-grey-500 lg:px-7',
        T.notOnMap,
      )
      els.results.appendChild(heading)
      offMap.forEach(function (group) {
        els.results.appendChild(buildRow(group))
      })
    }
  }

  // "144 groups worldwide" — count emphasised, scope muted.
  function setStatus(count, scope, actionNodes) {
    clear(els.status)
    if (count != null) {
      els.status.appendChild(el('span', 'font-medium text-mi-forest', groupCount(count)))
      if (scope) els.status.appendChild(document.createTextNode(' ' + scope))
    } else {
      els.status.textContent = scope
    }
    clear(els.statusActions)
    ;[].concat(actionNodes || []).forEach(function (node) {
      els.statusActions.appendChild(node)
    })
  }

  /* -------------------------------------------------------------------- map */

  function notice(text) {
    var panel = el('div', 'absolute inset-0 flex items-center justify-center p-8 pt-24')
    panel.appendChild(
      el(
        'p',
        'm-0 max-w-sm rounded border border-mi-grey-200 bg-white px-5 py-4 text-center text-sm leading-normal text-mi-charcoal',
        text,
      ),
    )
    return panel
  }

  function loadScript(src, integrity) {
    return new Promise(function (resolve, reject) {
      var script = document.createElement('script')
      script.src = src
      script.integrity = integrity
      script.crossOrigin = 'anonymous'
      script.async = true
      script.onload = resolve
      script.onerror = function () {
        reject(new Error('failed to load ' + src))
      }
      document.head.appendChild(script)
    })
  }

  function injectLeaflet() {
    if (window.L) return Promise.resolve(window.L)
    if (state.leafletFailed) return Promise.reject(new Error('leaflet unavailable'))

    if (!document.getElementById('cg-leaflet-css')) {
      var link = document.createElement('link')
      link.id = 'cg-leaflet-css'
      link.rel = 'stylesheet'
      link.href = LEAFLET_CSS
      link.integrity = LEAFLET_CSS_SRI
      link.crossOrigin = 'anonymous'
      document.head.appendChild(link)
    }

    return loadScript(LEAFLET_JS, LEAFLET_JS_SRI).then(function () {
      if (!window.L) throw new Error('leaflet loaded without L')
      // Clustering is a nicety — never let it take the map down with it.
      return loadScript(CLUSTER_JS, CLUSTER_JS_SRI).catch(function (error) {
        console.warn('community-groups: marker clustering unavailable', error)
      })
    })
  }

  function pointsFor(groups) {
    var points = []
    groups.forEach(function (group) {
      var point = group.slug ? state.pointsBySlug[group.slug] : null
      if (point) points.push({ group: group, lat: point.lat, lng: point.lng })
    })
    return points
  }

  // Only said when it's true: some listed groups can't be pinned.
  function updateCaption(mapped, total) {
    var caption = document.getElementById('cg-map-caption')
    if (!caption) return
    var unmapped = total - mapped
    caption.hidden = unmapped <= 0
    caption.textContent = unmapped > 0 ? fill(T.mapPartial, { __MAPPED__: mapped, __UNMAPPED__: unmapped }) : ''
  }

  function clusterIcon(cluster) {
    var count = cluster.getChildCount()
    var size = count >= 10 ? 36 : count >= 5 ? 30 : 26
    return window.L.divIcon({
      html: '<span>' + count + '</span>', // a number this file computed — never API data
      className: 'cg-cluster ' + (count >= 10 ? 'cg-cluster--lg' : 'cg-cluster--sm'),
      iconSize: [size, size],
    })
  }

  function renderMap(points, groups) {
    var container = el('div', 'absolute inset-0')
    container.id = 'cg-map'
    container.setAttribute('role', 'region')
    container.setAttribute('aria-label', T.mapTitle || '')

    clear(els.mapPanel)
    els.mapPanel.hidden = false
    els.mapPanel.appendChild(container)
    if (els.hint) els.hint.hidden = false
    updateCaption(points.length, groups.length)

    var L = window.L
    // The search bar sits across the top of the map, so zoom lives bottom-right.
    state.map = L.map(container, { scrollWheelZoom: false, zoomControl: false })
    L.control.zoom({ position: 'bottomright', zoomInTitle: T.zoomIn, zoomOutTitle: T.zoomOut }).addTo(state.map)
    // Drop the "Leaflet" prefix (optional). The OpenStreetMap credit is NOT
    // optional — the ODbL and the OSM tile policy both require it on the map.
    state.map.attributionControl.setPrefix(false)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18,
      attribution: T.mapAttribution,
    }).addTo(state.map)

    // Map → list. Only a move the visitor makes narrows the list; the fits this
    // file does after a filter change are flagged and ignored, otherwise the
    // filters and the map would keep re-triggering each other. A window resize
    // (invalidateSize) fires moveend without movestart, so it is ignored too.
    state.map.on('movestart', function () {
      if (!state.fitting) state.userMoving = true
    })
    state.map.on('moveend', function () {
      if (!state.userMoving) return
      state.userMoving = false
      state.area = state.map.getBounds()
      state.visible = LIST_PAGE
      refreshList()
    })

    state.markerLayer = L.markerClusterGroup
      ? L.markerClusterGroup({
          showCoverageOnHover: false,
          maxClusterRadius: 45,
          iconCreateFunction: clusterIcon,
        })
      : L.layerGroup()
    state.markerLayer.addTo(state.map)
    addMarkers(points)
  }

  // Popup "View this group in the list": reveal the row even if it sits past
  // "Show more", open it and scroll to it.
  function revealGroup(slug) {
    var row = document.getElementById('cg-group-' + slug)
    if (!row) {
      var list = applyFilters()
      var index = list.findIndex(function (group) {
        return group.slug === slug
      })
      if (index >= state.visible) {
        state.visible = index + 1
        refreshList()
        row = document.getElementById('cg-group-' + slug)
      }
    }
    if (!row) return
    if (row.cgOpen) row.cgOpen()
    row.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  function addMarkers(points) {
    var L = window.L
    state.markerLayer.clearLayers()

    var latLngs = []
    points.forEach(function (point) {
      // Circle markers are pure SVG: no marker-icon.png to resolve against
      // CDN-hosted CSS, which is the classic silent breakage.
      var marker = L.circleMarker([point.lat, point.lng], {
        radius: 7,
        color: '#FFFFFF',
        weight: 2,
        fillColor: '#297573',
        fillOpacity: 1,
      })

      // Popups carry no contact details — that is deliberate, and matches what
      // the geojson feature properties expose.
      var popup = el('div')
      popup.appendChild(el('strong', 'block text-mi-forest', point.group.name || ''))
      var place = locationLine(point.group)
      if (place) popup.appendChild(el('div', 'text-xs text-mi-grey-500', place))
      if (point.group.slug) {
        var link = el('a', 'mt-1 inline-block text-xs font-medium text-mi-deep-green underline', T.viewOnMap)
        link.href = '#cg-group-' + point.group.slug
        link.addEventListener('click', function (event) {
          event.preventDefault()
          revealGroup(point.group.slug)
        })
        popup.appendChild(link)
      }
      marker.bindPopup(popup)

      state.markerLayer.addLayer(marker)
      latLngs.push([point.lat, point.lng])
    })

    // Never zoom past what the active filter implies. Groups in one country often
    // share a city (Bulgaria's two are both in Sofia), and an uncapped fitBounds
    // would zoom to street level around them.
    // animate: false keeps the move synchronous, so `fitting` covers all of it.
    var maxZoom = fitZoomCap()
    state.fitting = true
    try {
      if (latLngs.length === 1) {
        state.map.setView(latLngs[0], maxZoom, { animate: false })
      } else if (latLngs.length > 1) {
        state.map.fitBounds(latLngs, { padding: [40, 40], maxZoom: maxZoom, animate: false })
      }
    } finally {
      state.fitting = false
    }
  }

  // A search that names a whole country or state/region shows all of it; any
  // other search stops at roughly metro level.
  function fitZoomCap() {
    var q = currentFilters().q
    if (!q) return 10
    var country = state.groups.some(function (group) {
      return group.country && String(group.country.name || '').toLowerCase() === q
    })
    if (country) return 6
    var region = state.groups.some(function (group) {
      return namesRegion(group, q)
    })
    return region ? 7 : 9
  }

  /**
   * Three states, never conflated:
   *   1. points available  → Leaflet map (+ a "not all mapped" caption if so)
   *   2. nothing to map    → neutral panel, Leaflet is NEVER requested
   *   3. directory failed  → the panel is hidden entirely (the error lives in
   *                          #cg-status with a Retry)
   * (2) becomes (1) by itself the first time the geojson endpoint returns a
   * feature — no theme change, nothing to un-hide.
   */
  function renderMapPanel(groups) {
    if (!CONFIG.showMap) {
      els.mapPanel.hidden = true
      return
    }

    var points = pointsFor(groups)

    if (!points.length) {
      if (state.map) {
        // Filters matched nothing mappable: keep the map, just empty it.
        state.markerLayer.clearLayers()
        updateCaption(0, groups.length)
        return
      }
      clear(els.mapPanel)
      els.mapPanel.hidden = false
      if (els.hint) els.hint.hidden = true
      els.mapPanel.appendChild(
        notice(state.leafletFailed ? T.mapError : fill(T.mapPending, { __COUNT__: groups.length })),
      )
      return
    }

    if (state.map) {
      addMarkers(points)
      updateCaption(points.length, groups.length)
      return
    }

    injectLeaflet()
      .then(function () {
        renderMap(points, groups)
      })
      .catch(function (error) {
        console.warn('community-groups: map unavailable', error)
        state.leafletFailed = true
        clear(els.mapPanel)
        els.mapPanel.hidden = false
        if (els.hint) els.hint.hidden = true
        els.mapPanel.appendChild(notice(T.mapError))
      })
  }

  /* ------------------------------------------------------------------- flow */

  // Filters changed (or the map was reset): drop the map area, re-fit the map.
  function update() {
    state.area = null
    state.visible = LIST_PAGE
    refreshList()
    renderMapPanel(applyFilters())
  }

  // List + status only. The map is left where it is.
  function refreshList() {
    var filtered = applyFilters()
    var filtersOn = anyFilterActive(currentFilters())
    var actions = filtersOn ? [clearFiltersButton()] : []

    els.resetMap.hidden = !state.area
    if (els.hint) els.hint.hidden = Boolean(state.area) || !state.map

    if (!state.area) {
      setStatus(filtered.length, filtersOn ? T.scopeFiltered : T.scopeAll, actions)
      renderResults(filtered)
      return
    }

    var inView = []
    var offMap = []
    filtered.forEach(function (group) {
      var point = group.slug ? state.pointsBySlug[group.slug] : null
      if (!point) offMap.push(group)
      else if (state.area.contains([point.lat, point.lng])) inView.push(group)
    })

    setStatus(inView.length, T.scopeArea, actions)
    renderResults(inView, offMap)
  }

  function init() {
    setStatus(null, T.loading, null)
    els.mapPanel.hidden = true
    clear(els.results)

    loadGroups()
      .then(function (result) {
        state.groups = (result.groups || []).slice().sort(function (a, b) {
          return String(a.name || '').localeCompare(String(b.name || ''))
        })
        state.total = result.total
        renderTypeChips()
        populateLanguages()
        update()

        // Independent of the directory: a geojson failure must never blank the list.
        return loadPoints(state.groups).then(function (bySlug) {
          state.pointsBySlug = bySlug
          renderMapPanel(applyFilters())
        })
      })
      .catch(function (error) {
        console.error('community-groups: failed to load directory', error)
        state.groups = []
        state.total = 0
        clear(els.results)
        els.mapPanel.hidden = true
        setStatus(null, T.errorLoading, retryButton())
      })
  }

  function start() {
    els.search = document.getElementById('cg-search')
    els.language = document.getElementById('cg-language')
    els.types = document.getElementById('cg-types')
    els.resetMap = document.getElementById('cg-reset-map')
    els.hint = document.getElementById('cg-map-hint')
    els.status = document.getElementById('cg-status')
    els.statusActions = document.getElementById('cg-status-actions')
    els.mapPanel = document.getElementById('cg-map-panel')
    els.results = document.getElementById('cg-results')

    if (!els.results || !els.search) return

    // The Liquid markup ships the placeholder option; keep its label for reuse
    // when the options are repopulated.
    var first = els.language.options[0]
    els.language.setAttribute('data-placeholder', first ? first.textContent : '')

    els.search.addEventListener('input', update)
    els.language.addEventListener('change', update)
    els.resetMap.addEventListener('click', update)

    init()
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start)
  } else {
    start()
  }
})()
